
































































































import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import GeneralInfo from "@/views/rate-changes/components/GeneralInfo.vue";
import RateChangeTable from "@/views/rate-changes/components/RateChangeTable.vue";
import { IPRCPriceChangeApproveDenyPayload, RateChangesState, SetValuePayload } from "@/models";
import AuthModule from "@storage360/login/src/store/modules/auth";
import RateChangesModule from "@/store/modules/rate-changes";
import { Component, Vue } from "vue-property-decorator";
import { namespace, State } from "vuex-class";
import { approvalRoles } from "@/helpers/iprc/approval-roles";

const auth = namespace(AuthModule.name);
const rateChanges = namespace(RateChangesModule.name);

@Component({
  name: "ConfirmChange",
  components: { ApplicationLayout, GeneralInfo, RateChangeTable }
})
export default class ConfirmChange extends Vue {
  // ===== Store ===== //
  @auth.Getter("allowedAccess") private allowedAccess!: string[];
  @State("rateChanges") private rateChanges!: RateChangesState;
  @rateChanges.Action("approveIPRCSetPriceChanges")
    private approveIPRCSetPriceChanges!: (payload: IPRCPriceChangeApproveDenyPayload) => void;
  @rateChanges.Action("denyIPRCSetPriceChanges")
    private denyIPRCSetPriceChanges!: (payload: IPRCPriceChangeApproveDenyPayload) => void;
  @rateChanges.Action("getInlinePriceChange") private getInlinePriceChange!: (id: number) => void;
  @rateChanges.Action("resetFilters") private resetFilters!: () => void;
  @rateChanges.Action("resetReviewedIPRCPriceChanges") private resetReviewedIPRCPriceChanges!: () => void;
  @rateChanges.Action("setSelectedTab") private setSelectedTab!: (selectedTab: number) => void;
  @rateChanges.Action("setValue") private setValue!: (payload: SetValuePayload) => void;
  @rateChanges.Action("submitIPRCSetForApproval") private submitIPRCSetForApproval!: (payload: number) => void;
  // ===== Data ===== //
  private denyPriceChangeModal = false;
  private denialReason = "";
  // ===== Methods ===== //
  private closeDenyPriceChangeModal(): void {
    this.denyPriceChangeModal = false;
    this.denialReason = "";
  }
  private async handleApprovalAndProcessing(): Promise<void> {
    const payload: IPRCPriceChangeApproveDenyPayload = {
      id: this.selectedIPRCSetId,
      approved: true
    };
    await this.approveIPRCSetPriceChanges(payload);
    this.$router.push({ name: "rateChanges" }).catch(() => { return true; });
  }
  private handleCancel(): void {
    if (this.approvalView) {
      this.resetReviewedIPRCPriceChanges();
      this.$router.push({ name: "rateChanges" }).catch(() => { return true; });
    } else {
      this.getInlinePriceChange(this.selectedIPRCSetId);
      this.setSelectedTab(0);
      this.$router.push({
        name: "rateChange",
        params: {
          id: String(this.selectedIPRCSetId)
        }
      }).catch(() => { return true; });
    }
  }
  private async handleCreateRequest(): Promise<void> {
    await this.submitIPRCSetForApproval(this.selectedIPRCSetId);
    this.$router.push({ name: "rateChanges" }).catch(() => { return true; });
  }
  private async handleDenyChangeSet(): Promise<void> {
    const payload: IPRCPriceChangeApproveDenyPayload = {
      id: this.selectedIPRCSetId,
      approved: false,
      denial_reason: this.denialReason
    };
    this.denyIPRCSetPriceChanges(payload);
    this.closeDenyPriceChangeModal();
    this.resetReviewedIPRCPriceChanges();
    this.$router.push({ name: "rateChanges" }).catch(() => { return true; });
  }
  private handleReturnAllChangeSets(): void {
    this.resetReviewedIPRCPriceChanges();
    this.$router.push({ name: "rateChanges" }).catch(() => { return true; });
  }
  // ===== Computed ===== //
  get approvalView(): boolean {
    return !!(this.isRevManagerApprover && this.rateChanges.reviewedIPRCSetPriceChanges.length);
  }
  get changeSetDeniedStatus(): boolean {
    return this.selectedIPRCSetStatus.toLowerCase().includes("denied");
  }
  get changeSetQueuedStatus(): boolean {
    return this.selectedIPRCSetStatus.toLowerCase().includes("price change set queued");
  }
  get denialComplete(): boolean {
    return !!(this.denialReason && this.denialReason.length);
  }
  get errorProcessingStatus(): boolean {
    const status = this.selectedIPRCSetStatus.toLowerCase();
    return status.includes("unable to process price change set") || status.includes("error");
  }
  get hideButtonsPriceChangeStatus(): boolean {
    const status = this.changeSetQueuedStatus || this.pricesUpdatedStatus || this.changeSetDeniedStatus ||
      this.errorProcessingStatus || this.revertStatus;
    if (!this.approvalView) {
      return status || this.submittedReviewStatus || this.newPriceStatus;
    }
    return status;
  }
  get isRevManagerApprover(): boolean {
    return approvalRoles.some(role => this.allowedAccess.includes(role));
  }
  get newPriceStatus(): boolean {
    return this.selectedIPRCSetStatus.toLowerCase().includes("new price change set");
  }
  get pricesUpdatedStatus(): boolean {
    return this.selectedIPRCSetStatus.toLowerCase().includes("prices updated");
  }
  get revertStatus(): boolean {
    return this.selectedIPRCSetStatus.toLowerCase().includes("revert");
  }
  get selectedIPRCSetId(): number {
    return this.rateChanges.selectedIPRCSet!.id;
  }
  get selectedIPRCSetStatus(): string {
    return this.rateChanges.selectedIPRCSet! && this.rateChanges.selectedIPRCSet!.status;
  }
  get submittedReviewStatus(): boolean {
    return this.selectedIPRCSetStatus.toLowerCase().includes("submitted for review");
  }
  get statusUpdate(): string {
    if (this.approvalView && !this.hideButtonsPriceChangeStatus) {
      return "Ready for Approval and Processing";
    } else if (this.errorProcessingStatus) {
      return "Unable to process Price Change";
    } else if (this.pricesUpdatedStatus) {
      return "Price Change Updated";
    } else if (this.changeSetQueuedStatus) {
      return "Approved and Processing";
    } else if (this.changeSetDeniedStatus) {
      return "Denied Price Change";
    } else if (this.revertStatus) {
      return "Price Change Reverted";
    }

    return "Ready for Submission";
  }
  get statusUpdateColors(): string {
    return !this.statusUpdate.toLowerCase().includes("denied") && !this.statusUpdate.toLowerCase().includes("unable")
      ? "success--text" : "error--text";
  }

  // ===== Lifecycle Hooks ===== //
  private beforeDestroy() {
    this.resetFilters();
    this.setSelectedTab(0);
    this.setValue({ property: "changeDate", value: "" });
    this.setValue({ property: "changeReason", value: "" });
    this.setValue({ property: "ledgers", value: [] });
  }
}
